import { useRef, useState } from "react";
import { Trans } from "react-i18next";
import { IconVideoEmpty } from "@icons/index";
import IconVideo from "@components/Image/icon-video.svg?react";
import { ClipLoading } from "@src/ui/Loading";

export interface PostToTikTokVideoPreviewProps {
  videoUrl?: string;
  posterUrl?: string;
  loading?: boolean;
}

export const PostToTikTokVideoPreview = ({ videoUrl, posterUrl, loading }: PostToTikTokVideoPreviewProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const syncPlayingState = () => {
    setPlaying(!(videoRef.current?.paused ?? true));
  };

  const handleTogglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      void video.play().catch(() => {});
      return;
    }
    video.pause();
  };

  return (
    <aside className="flex w-[273px] shrink-0 flex-col items-center gap-[12px] border-r border-kds-border p-[24px]">
      <div className="relative h-[400px] w-[225px] overflow-hidden rounded-[8px] bg-kds-bg-subtle">
        {loading ? (
          <ClipLoading className="size-full" />
        ) : videoUrl ? (
          <>
            <video
              ref={videoRef}
              src={videoUrl}
              poster={posterUrl}
              className="absolute left-1/2 top-0 h-full w-[238px] max-w-none -translate-x-1/2 object-cover"
              playsInline
              preload="metadata"
              onPlay={syncPlayingState}
              onPause={syncPlayingState}
              onEnded={syncPlayingState}
            />
            <button
              type="button"
              className="absolute inset-0 flex cursor-pointer items-center justify-center border-0 bg-transparent p-0"
              onClick={handleTogglePlay}
              aria-label={playing ? "Pause video preview" : "Play video preview"}
            >
              {!playing ? <IconVideo className="size-[48px] opacity-80" /> : null}
            </button>
          </>
        ) : (
          <div className="flex size-full flex-col items-center justify-center gap-[8px] px-[16px] text-center">
            <IconVideoEmpty className="size-[48px] shrink-0 opacity-80" />
            <span className="text-[12px] leading-[16px] text-kds-fg-subtle">
              <Trans>Video preview unavailable</Trans>
            </span>
          </div>
        )}
      </div>
    </aside>
  );
};
